/**
 * JOB HUNTER › SOURCES — /job-hunter/sources
 *
 * Where jobs come from. Tier 1 = employers' own careers systems (only their HR
 * can post there, so no fakes); tier 2/3 = curated boards and aggregators used
 * to discover employers. Polling follows "heat": hot every 6 h, warm daily,
 * cold weekly, dead never (until revived). Below: every agent run.
 */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Check, ChevronDown, ChevronRight, Database, ExternalLink, Flame, Globe, Landmark, LoaderCircle, Plus, RefreshCw, Rss, Trash2, TriangleAlert, Zap } from 'lucide-react';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { JobHunterShell, RunAgentButton, useRunFinished } from '@/components/job-hunter/JobHunterShell';
import { CountryTag, Field, HeatBadge, Pill, Segmented, Toggle, JhSelect } from '@/components/job-hunter/ui';
import { CountryMultiPicker } from '@/components/CountryMultiPicker';
import { useJobHunter } from '@/lib/job-hunter/store';
import { HEAT_META, countryName, flag, fmtDateTime, num, timeAgo } from '@/lib/job-hunter/format';
import { jobHunterService, type JhCatalogEntry, type JhHeat, type JhRegisterStatus, type JhRun, type JhSource, type JhSourceType } from '@/services/job-hunter.service';
import { useAuth } from '@/hooks/useAuth';

const INTERVALS = [
  { v: '', l: 'Auto (by heat)' }, { v: '360', l: 'Every 6 h' }, { v: '720', l: 'Every 12 h' }, { v: '1440', l: 'Daily' }, { v: '4320', l: 'Every 3 days' }, { v: '10080', l: 'Weekly' },
];

function AddSource({ open, onClose, onSaved, catalog, existing }: { open: boolean; onClose: () => void; onSaved: () => void; catalog: JhCatalogEntry[]; existing: JhSource[] }) {
  const [mode, setMode] = useState<'link' | 'slug' | 'board'>('link');
  const [url, setUrl] = useState('');
  const [employerName, setEmployerName] = useState('');
  const [type, setType] = useState<JhSourceType>('greenhouse');
  const [slug, setSlug] = useState('');
  const [region, setRegion] = useState('');
  const boards = catalog.filter((c) => !c.ats && !existing.some((s) => s.type === c.type));
  const [board, setBoard] = useState<JhSourceType | ''>('');
  const [visaOnly, setVisaOnly] = useState(true);
  const [pages, setPages] = useState(3);
  const [appId, setAppId] = useState('');
  const [appKey, setAppKey] = useState('');
  const [countries, setCountries] = useState<string[]>(['DE', 'NL', 'IT', 'AT', 'PL']);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open && !board && boards[0]) setBoard(boards[0].type); }, [open, board, boards]);

  const save = async () => {
    setBusy(true);
    try {
      let r;
      if (mode === 'link') r = await jobHunterService.createSource({ url: url.trim(), employerName: employerName.trim() || undefined });
      else if (mode === 'slug') r = await jobHunterService.createSource({ type, slug: slug.trim(), employerName: employerName.trim() || undefined, region: region || undefined });
      else {
        const config: Record<string, unknown> = board === 'arbeitnow' ? { visaOnly, pages } : board === 'adzuna' ? { appId, appKey, countries } : {};
        r = await jobHunterService.createSource({ type: board as JhSourceType, config });
      }
      toast.success(r.openJobs !== null ? `Source added — ${r.openJobs} open job${r.openJobs === 1 ? '' : 's'} on that board` : 'Source added');
      setUrl(''); setSlug(''); setEmployerName('');
      onSaved(); onClose();
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  const can = mode === 'link' ? /^https?:\/\//.test(url.trim()) : mode === 'slug' ? !!slug.trim() : !!board && (board !== 'adzuna' || (!!appId && !!appKey));
  const atsTypes = catalog.filter((c) => c.ats);

  return (
    <Modal open={open} onClose={onClose} title="Add a source" widthClass="max-w-xl">
      <Segmented value={mode} onChange={setMode} options={[{ value: 'link', label: 'Careers board link' }, { value: 'slug', label: 'ATS + slug' }, { value: 'board', label: 'Job board' }]} />
      <div className="mt-4 space-y-4">
        {mode === 'link' && <>
          <Field label="Careers board link" hint="e.g. https://jobs.lever.co/pipedrive, https://boards.greenhouse.io/adyen, https://jobs.ashbyhq.com/stream, https://company.recruitee.com">
            <input className="jh-input" autoFocus value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" />
          </Field>
          <Field label="Employer name (optional)"><input className="jh-input" value={employerName} onChange={(e) => setEmployerName(e.target.value)} placeholder="Taken from the feed when blank" /></Field>
        </>}
        {mode === 'slug' && <div className="grid gap-4 sm:grid-cols-2">
          <Field label="ATS"><JhSelect value={type} onChange={setType} options={atsTypes.map((c) => ({ value: c.type, label: c.label }))} /></Field>
          <Field label="Board slug" hint="The part after the ATS domain"><input className="jh-input" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="e.g. adyen" /></Field>
          <Field label="Employer name"><input className="jh-input" value={employerName} onChange={(e) => setEmployerName(e.target.value)} /></Field>
          {type === 'lever' && <Field label="Lever region"><JhSelect value={region} onChange={setRegion} options={[{ value: '', label: 'Global', description: 'jobs.lever.co' }, { value: 'eu', label: 'EU', description: 'jobs.eu.lever.co' }]} /></Field>}
        </div>}
        {mode === 'board' && (boards.length === 0 ? <p className="jh-muted">Every board is already a source.</p> : <>
          <Field label="Board">
            <JhSelect value={board} onChange={(v) => setBoard(v as JhSourceType)} options={boards.map((c) => ({ value: c.type as string, label: c.label, description: c.description }))} />
          </Field>
          {board === 'arbeitnow' && <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-border px-3 py-2.5"><span className="text-[13px] font-semibold">Visa-sponsorship jobs only</span><Toggle checked={visaOnly} onChange={setVisaOnly} /></div>
            <Field label="Pages per poll"><JhSelect value={pages} onChange={setPages} options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: String(n), description: `~${n * 100} jobs` }))} /></Field>
          </div>}
          {board === 'adzuna' && <div className="grid gap-4 sm:grid-cols-2">
            <Field label="App ID" hint="From your Adzuna API plan (paid) — optional; the ATS feeds and boards work without it"><input className="jh-input" value={appId} onChange={(e) => setAppId(e.target.value)} /></Field>
            <Field label="App key" hint="Stored encrypted"><input className="jh-input" type="password" value={appKey} onChange={(e) => setAppKey(e.target.value)} /></Field>
            <Field label="Countries to search" className="sm:col-span-2"><CountryMultiPicker value={countries} onChange={setCountries} /></Field>
          </div>}
        </>)}
      </div>
      <div className="mt-6 flex justify-end gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!can} onClick={save}>Add source</Button></div>
    </Modal>
  );
}

/** Where a source's jobs are: the employer's country, or the board's market. */
function SourceCountry({ s }: { s: JhSource }) {
  if (s.employer?.country) return <CountryTag cc={s.employer.country} />;
  if (s.type === 'arbeitnow') return <CountryTag cc="DE" />;
  if (s.type === 'remotive' || s.type === 'himalayas') return <span className="inline-flex items-center gap-1.5 text-xs text-text-secondary"><Globe size={13} />Remote · worldwide</span>;
  const list = Array.isArray(s.config.countries) ? (s.config.countries as string[]).map((c) => c.toUpperCase()) : [];
  if (list.length) return <span className="text-xs text-text-secondary" title={list.map(countryName).join(', ')}>{list.map((c) => flag(c)).join(' ')}</span>;
  return <span className="text-xs text-text-muted">—</span>;
}

function SourceRow({ s, onChanged }: { s: JhSource; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  const act = async (key: string, fn: () => Promise<unknown>, ok?: string) => {
    setBusy(key);
    try { await fn(); if (ok) toast.success(ok); onChanged(); } catch { /* toasted */ } finally { setBusy(null); }
  };
  const poll = () => act('poll', async () => {
    const r = await jobHunterService.pollSource(s.sourceUid);
    if (r.ok) toast.success(`${s.name}: ${r.fetched} jobs read · ${r.created} new · ${r.hits} title matches${r.closed ? ` · ${r.closed} closed` : ''}`);
    else toast.error(`${s.name}: ${r.error}`);
  });
  return (
    <tr className={s.enabled ? '' : 'opacity-60'}>
      <td>
        <div className="font-semibold">{s.employer?.name ?? s.name}</div>
        <div className="mt-0.5 flex items-center gap-1.5 text-[11.5px] text-text-muted"><Pill tone={s.tier === 1 ? 'success' : s.tier === 2 ? 'info' : 'neutral'}>{s.label}</Pill>{s.tier === 1 && <span className="font-mono">{s.key}</span>}</div>
      </td>
      <td><SourceCountry s={s} /></td>
      <td><HeatBadge heat={s.heat} /></td>
      <td className="whitespace-nowrap text-text-secondary">{timeAgo(s.lastPolledAt)}{s.consecutiveFailures > 0 && <div className="mt-0.5 max-w-[240px] truncate text-[11px] text-danger" title={s.lastError}><TriangleAlert size={11} className="mr-1 inline" />{s.lastError}</div>}</td>
      <td className="num">{num(s.lastJobCount)}<div className="text-[11px] text-text-muted">+{s.lastNewCount} new</div></td>
      <td className="num">{num(s.totalProposed)}</td>
      <td className="whitespace-nowrap text-text-muted">{s.enabled && s.heat !== 'dead' ? timeAgo(s.nextPollAt) : '—'}</td>
      <td>
        <JhSelect size="sm" className="w-36" ariaLabel="Poll interval" value={s.pollIntervalMin ? String(s.pollIntervalMin) : ''}
          onChange={(v) => act('interval', () => jobHunterService.updateSource(s.sourceUid, { pollIntervalMin: v ? Number(v) : null }), 'Poll interval saved')}
          options={INTERVALS.map((i) => ({ value: i.v, label: i.l }))} />
      </td>
      <td>
        <div className="flex items-center justify-end gap-1.5">
          <Toggle checked={s.enabled} label="Enabled" disabled={!!busy} onChange={(v) => act('toggle', () => jobHunterService.updateSource(s.sourceUid, { enabled: v }), v ? 'Source on' : 'Source paused')} />
          {s.heat === 'dead'
            ? <Button size="sm" variant="secondary" loading={busy === 'revive'} onClick={() => act('revive', () => jobHunterService.updateSource(s.sourceUid, { revive: true }), 'Revived — polled on the next run')}>Revive</Button>
            : <button type="button" title="Poll now" disabled={!!busy} onClick={poll} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-secondary hover:border-primary hover:text-primary disabled:opacity-50">{busy === 'poll' ? <LoaderCircle size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>}
          <button type="button" title="Delete" disabled={!!busy} onClick={() => { if (window.confirm(`Remove “${s.name}” as a source? Jobs already found stay.`)) void act('delete', () => jobHunterService.deleteSource(s.sourceUid), 'Source removed'); }} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-text-muted hover:border-danger hover:text-danger disabled:opacity-50"><Trash2 size={14} /></button>
        </div>
      </td>
    </tr>
  );
}

/** "Load starter set" — the boards plus verified employer feeds, for the regions picked. */
function SeedModal({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { meta } = useJobHunter();
  const regions = meta?.seedRegions ?? [];
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setPicked(regions.map((r) => r.key)); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const total = regions.filter((r) => picked.includes(r.key)).reduce((n, r) => n + r.employers, 0);
  const seed = async () => {
    setBusy(true);
    try {
      const r = await jobHunterService.seedSources(picked.length === regions.length ? undefined : picked);
      toast.success(r.created ? `${r.created} sources added — the agents start polling them on the next run` : 'Those sources are already loaded');
      onDone(); onClose();
    } catch { /* toasted */ } finally { setBusy(false); }
  };
  return (
    <Modal open={open} onClose={onClose} title="Load the starter set" widthClass="max-w-lg">
      <p className="text-[13px] leading-relaxed text-text-secondary">Adds the job boards (Arbeitnow visa filter, Remotive, Himalayas) and the tech employers whose public careers feeds were verified live — known to hire from abroad. Pick the regions your agents target; you can load more later.</p>
      <div className="mt-4 space-y-2">
        {regions.map((r) => {
          const on = picked.includes(r.key);
          return (
            <button key={r.key} type="button" onClick={() => setPicked((p) => (on ? p.filter((x) => x !== r.key) : [...p, r.key]))}
              className={clsx('flex w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition', on ? 'border-primary bg-primary-soft/50' : 'border-border hover:border-border-strong')}>
              <span className={clsx('grid h-5 w-5 shrink-0 place-items-center rounded-md border', on ? 'border-primary bg-primary text-on-primary' : 'border-border-strong')}>{on && <Check size={13} />}</span>
              <span className="min-w-0 flex-1 text-[13.5px] font-semibold">{r.label}</span>
              <span className="jh-chip">{r.employers} employers</span>
            </button>
          );
        })}
      </div>
      <div className="mt-5 flex items-center justify-between gap-2">
        <span className="text-xs text-text-muted">{total} employer feeds + the boards</span>
        <div className="flex gap-2"><Button variant="ghost" onClick={onClose}>Cancel</Button><Button loading={busy} disabled={!picked.length} iconLeft={<Database size={14} />} onClick={seed}>Load</Button></div>
      </div>
    </Modal>
  );
}

/** Official sponsor registers — shared public data; the agents check employers against them. */
function RegistersCard() {
  const { user } = useAuth();
  const [st, setSt] = useState<JhRegisterStatus | null>(null);
  const [starting, setStarting] = useState(false);
  const load = useCallback(async () => { try { setSt(await jobHunterService.registers()); } catch { /* toasted */ } }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (!st?.importing) return;
    const t = window.setInterval(() => void load(), 4000);
    return () => window.clearInterval(t);
  }, [st?.importing, load]);
  const refresh = async () => {
    setStarting(true);
    try { await jobHunterService.importRegisters(true); toast.success('Importing the registers in the background — this takes a minute'); window.setTimeout(() => void load(), 1500); }
    catch { /* toasted */ } finally { setStarting(false); }
  };
  return (
    <section className="jh-card mb-5 overflow-hidden">
      <div className="jh-card-pad flex flex-wrap items-start justify-between gap-2 !pb-3">
        <div>
          <div className="jh-card-title"><Landmark size={16} className="text-success" />Official sponsor registers</div>
          <div className="jh-card-sub max-w-2xl">Government lists of employers allowed to sponsor work visas — UK Home Office, Dutch IND, US H-1B employer data. The agents check each employer against them and show the entry as evidence on matches. Refreshed weekly.</div>
        </div>
        {user?.role === 'company_admin' && <Button size="sm" variant="secondary" loading={starting || !!st?.importing} iconLeft={<RefreshCw size={13} />} onClick={refresh}>{st?.importing ? 'Importing…' : 'Refresh registers'}</Button>}
      </div>
      <div className="overflow-x-auto">
        <table className="jh-table min-w-[640px]">
          <thead><tr><th>Register</th><th className="num">Employers</th><th>Last import</th><th /></tr></thead>
          <tbody>
            {!st ? <tr><td colSpan={4}><div className="h-6 animate-pulse rounded bg-surface-alt" /></td></tr> : st.registers.map((r) => (
              <tr key={r.country}>
                <td><div className="flex items-center gap-2"><span className="text-[17px]">{flag(r.country)}</span><div className="min-w-0"><div className="font-semibold">{r.name}</div><div className="text-[11px] text-text-muted">{r.route}</div></div></div></td>
                <td className="num font-semibold">{r.rows ? num(r.rows) : '—'}</td>
                <td className="whitespace-nowrap">
                  {r.importedAt ? <span className="text-text-secondary">{timeAgo(r.importedAt)}</span> : <span className="text-text-muted">Never</span>}
                  {r.lastError && <div className="mt-0.5 max-w-[320px] truncate text-[11px] text-danger" title={r.lastError}><TriangleAlert size={11} className="mr-1 inline" />{r.lastError}</div>}
                </td>
                <td className="text-right"><a href={r.page} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-primary">Source <ExternalLink size={11} /></a></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RunsLog() {
  const [runs, setRuns] = useState<JhRun[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => { try { setRuns((await jobHunterService.runs(40)).runs); } catch { setRuns([]); } }, []);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);
  return (
    <section id="runs" className="jh-card mt-6 overflow-hidden">
      <div className="jh-card-pad flex flex-wrap items-center justify-between gap-2 !pb-3">
        <div><div className="jh-card-title"><Zap size={16} className="text-primary" />Agent runs</div><div className="jh-card-sub">Scheduled passes run every minute when something is due; manual runs come from “Run agent now”.</div></div>
        <Button size="sm" variant="ghost" iconLeft={<RefreshCw size={13} />} onClick={() => void load()}>Refresh</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="jh-table min-w-[860px]">
          <thead><tr><th style={{ width: 28 }} /><th>Started</th><th>Trigger</th><th className="num">Sources</th><th className="num">New jobs</th><th className="num">Screened</th><th className="num">Scored</th><th className="num">Matches</th><th className="num">Alerts</th><th className="num">AI calls</th></tr></thead>
          <tbody>
            {runs === null ? <tr><td colSpan={10}><div className="h-6 animate-pulse rounded bg-surface-alt" /></td></tr>
              : runs.length === 0 ? <tr><td colSpan={10} className="py-8 text-center jh-muted">No runs yet.</td></tr>
              : runs.map((r) => (
                <Fragment key={r.runUid}>
                  <tr className={r.errorLog.length ? 'cursor-pointer' : ''} onClick={() => r.errorLog.length && setOpen(open === r.runUid ? null : r.runUid)}>
                    <td>{r.errorLog.length ? (open === r.runUid ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : null}</td>
                    <td className="whitespace-nowrap">{r.finishedAt ? fmtDateTime(r.startedAt) : <span className="inline-flex items-center gap-2"><span className="jh-pulse" />Running…</span>}</td>
                    <td><Pill tone={r.trigger === 'manual' ? 'primary' : 'neutral'}>{r.trigger}</Pill>{r.errorLog.length > 0 && <Pill tone="warning" className="ml-1.5">{r.errorLog.length} issue{r.errorLog.length === 1 ? '' : 's'}</Pill>}</td>
                    <td className="num">{r.sourcesPolled}</td><td className="num">{r.jobsNew}</td><td className="num">{r.screened}</td><td className="num">{r.scored}</td>
                    <td className="num font-bold">{r.proposed}</td><td className="num">{r.notified}</td><td className="num">{r.aiCalls}</td>
                  </tr>
                  {open === r.runUid && <tr><td colSpan={10} className="!bg-surface-alt/50"><ul className="space-y-1 text-[12px] text-danger">{r.errorLog.map((e, i) => <li key={i}>• {e}</li>)}</ul></td></tr>}
                </Fragment>
              ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function JhSources() {
  const { meta } = useJobHunter();
  const [data, setData] = useState<{ rows: JhSource[]; heat: Partial<Record<JhHeat, number>>; catalog: JhCatalogEntry[] } | null>(null);
  const [adding, setAdding] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [filter, setFilter] = useState<'all' | JhHeat>('all');

  const load = useCallback(async () => { try { setData(await jobHunterService.sources()); } catch { setData({ rows: [], heat: {}, catalog: [] }); } }, []);
  useEffect(() => { void load(); }, [load]);
  useRunFinished(load);
  useEffect(() => { if (window.location.hash === '#runs') window.setTimeout(() => document.getElementById('runs')?.scrollIntoView({ behavior: 'smooth' }), 400); }, []);

  const seed = () => setSeeding(true);

  const rows = useMemo(() => (data?.rows ?? []).filter((r) => filter === 'all' || r.heat === filter), [data, filter]);
  const boards = rows.filter((r) => r.tier !== 1);
  const feeds = rows.filter((r) => r.tier === 1);
  const h = data?.heat ?? {};

  const table = (list: JhSource[], empty: string) => (
    <div className="overflow-x-auto">
      <table className="jh-table min-w-[1100px]">
        <thead><tr><th>Source</th><th>Country</th><th>Heat</th><th>Last poll</th><th className="num">Jobs read</th><th className="num">Matches</th><th>Next</th><th>Interval</th><th /></tr></thead>
        <tbody>{list.length ? list.map((s) => <SourceRow key={s.sourceUid} s={s} onChanged={() => void load()} />) : <tr><td colSpan={9} className="py-8 text-center jh-muted">{empty}</td></tr>}</tbody>
      </table>
    </div>
  );

  return (
    <JobHunterShell section="Sources" description="Employer careers feeds and job boards the agent reads"
      actions={<><Button size="sm" variant="secondary" iconLeft={<Database size={14} />} onClick={seed}>Load starter set</Button><Button size="sm" iconLeft={<Plus size={14} />} onClick={() => setAdding(true)}>Add source</Button></>}>
      {data === null ? <div className="grid min-h-[30vh] place-items-center"><LoaderCircle className="animate-spin text-primary" size={24} /></div> : (
        <>
          {data.rows.length === 0 ? (
            <div className="jh-hero text-center">
              <Rss className="mx-auto text-primary" size={28} />
              <div className="mt-3 text-[15px] font-semibold">No sources yet</div>
              <p className="jh-muted mx-auto mt-1 max-w-lg">The starter set adds Arbeitnow (visa-sponsorship filter), Remotive and Himalayas, plus tech employers whose public careers feeds were verified live — Europe (Adyen, N26, ABOUT YOU…), the UK (Monzo, Wise, Deliveroo…), USA & Canada (Stripe, Figma, Wealthsimple…) and Australia & New Zealand (Canva, Airwallex, Xero…).</p>
              <div className="mt-4 flex justify-center gap-2"><Button iconLeft={<Database size={14} />} onClick={seed}>Load starter set</Button><Button variant="secondary" iconLeft={<Plus size={14} />} onClick={() => setAdding(true)}>Add your own</Button></div>
            </div>
          ) : (
            <>
              <div className="jh-kpis mb-4">
                {(['hot', 'warm', 'cold', 'dead'] as JhHeat[]).map((k) => (
                  <button key={k} type="button" className="jh-kpi text-left" aria-pressed={filter === k} onClick={() => setFilter(filter === k ? 'all' : k)} style={filter === k ? { borderColor: 'var(--color-primary)' } : undefined}>
                    <div className="jh-kpi-label">{HEAT_META[k].label}<HeatBadge heat={k} /></div>
                    <div className="jh-kpi-value">{h[k] ?? 0}</div>
                    <div className="jh-kpi-hint">{HEAT_META[k].hint}</div>
                  </button>
                ))}
                <div className="jh-kpi flex flex-col justify-between">
                  <div className="jh-kpi-label">Run the agent<Flame size={14} className="text-primary" /></div>
                  <div className="mt-2"><RunAgentButton size="sm" /></div>
                </div>
              </div>
              <section className="jh-card mb-5 overflow-hidden">
                <div className="jh-card-pad !pb-3"><div className="jh-card-title"><Globe size={16} className="text-primary" />Job boards <span className="jh-chip">{boards.length}</span></div><div className="jh-card-sub">Discovery: they surface employers. A board posting is re-checked before you see it.</div></div>
                {table(boards, 'No boards — add Arbeitnow, Remotive, Himalayas or Adzuna.')}
              </section>
              <section className="jh-card overflow-hidden">
                <div className="jh-card-pad !pb-3"><div className="jh-card-title"><Rss size={16} className="text-success" />Employer careers feeds <span className="jh-chip">{feeds.length}</span></div><div className="jh-card-sub">Fake-proof: read straight from Greenhouse, Lever, Ashby, Workable, SmartRecruiters or Recruitee. New ones appear when the agent finds an employer that sponsors.</div></div>
                {table(feeds, 'No employer feeds yet.')}
              </section>
            </>
          )}
          <RegistersCard />
          <RunsLog />
        </>
      )}
      <SeedModal open={seeding} onClose={() => setSeeding(false)} onDone={() => void load()} />
      <AddSource open={adding} onClose={() => setAdding(false)} onSaved={() => void load()} catalog={data?.catalog ?? meta?.catalog ?? []} existing={data?.rows ?? []} />
    </JobHunterShell>
  );
}
